import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import {
  Writeback,
  WritebackRunStatus,
  WritebackRun,
  WritebackMode,
  WritebackRunOverallStatusType,
} from "@coeff/api";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";

import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card as _Card,
  CardHeader as _CardHeader,
  ImportTypeIcon,
  PushIcon,
  Table,
  Tag,
  WarningMessage,
  ErrorMessage,
  ConfirmBox,
  LoaderWithPerfTimings,
  FlexRow,
} from "../../../../components";
import { WritebackDetailsSidebarContent } from "../../../../components/WritebackDetailsSidebarContent";
import { COLORS } from "../../../../constants";
import { getRefreshedDate, useTrack } from "../../../../utils";
import { Header } from "../../components";
import { useDashboardContext } from "../../Dashboard";

const Sidebar = styled.div`
  width: 300px;
  position: absolute;
  right: 0px;
  top: 0px;
  bottom: 0px;
  border-left: 2px solid ${COLORS.black15};
  background: white;
  font-size: 13px;

  .subText {
    font-size: 12px;
    color: ${COLORS.black45};
    margin: 4px 0px;
  }
`;

const Wrap = styled.div`
  .title {
    font-weight: bold;
    font-size: 16px;
  }
`;

const LogoWrap = styled.div`
  position: relative;

  .coeff__push_icon {
    width: 15px;
    height: 12px;
    position: absolute;
    bottom: 0px;
    right: 0px;
  }
`;

const StyledTag = styled(Tag)`
  font-size: 16px;
  height: 24px;
  line-height: 24px;
  border: none;
  font-weight: 500;
  margin: 6px;

  .text {
    color: ${COLORS.black85};
    font-size: 14px;
  }
`;

const StyledLink = styled.div`
  font-size: 14px;
  margin: 6px;
`;

export const WritebackDetailsPage = ({ writebacks }: { writebacks: Writeback[] }) => {
  const track = useTrack();

  const { getWritebacksRunHistory, downloadRunHistory, spreadsheets } = useDashboardContext();

  const [writebackRuns, setWritebackRuns] = useState<WritebackRun[]>([]);

  const [loading, setLoading] = useState<boolean>(true);

  const [showFailedRunError, setShowFailedRunError] = useState<WritebackRunStatus | null>(null);

  const history = useHistory();

  const params = useParams<{ writebackId: string; ssId: string }>();

  const writeback = writebacks.find(di => di.writeback_id === params.writebackId);

  const spreadsheet = spreadsheets?.find(ss => ss.google_sheets_file_id === params.ssId);

  useEffect(() => {
    if (writeback && spreadsheet) {
      track("workspace_wb_viewed", {
        writeback_id: writeback?.writeback_id,
        ss_id: spreadsheet.google_sheets_file_id,
        ss_name: spreadsheet.google_sheets_file_name,
      });
    }
  }, [writeback, spreadsheet]);

  useEffect(() => {
    const getRuns = async () => {
      if (writeback) {
        setLoading(true);

        const { writeback_runs } = await getWritebacksRunHistory(writeback?.writeback_id ?? "", 1);

        setWritebackRuns(writeback_runs);
        setLoading(false);
      }

      if (!writeback && writebacks.length > 0) {
        history.push(`/dashboard/spreadsheets/${spreadsheet?.google_sheets_file_id}`);
      }
    };
    getRuns();
  }, [writeback, writebacks]);

  const onOpenSheetClick = () => {
    track("workspace_open_sheet_clicked", {
      event_from: "writeback_details",
      writeback_id: writeback?.writeback_id,
      ss_id: spreadsheet?.google_sheets_file_id,
      ss_name: spreadsheet?.google_sheets_file_name,
    });

    window.open(writeback?.writeback_tab_url ?? "", "_blank");
  };

  const onWritebackRunPageNumberChange = async (page: number, pageSize: number) => {
    const isCurrentlyOnLastPage = page === Math.ceil(writebackRuns.length / pageSize);

    if (isCurrentlyOnLastPage) {
      const { writeback_runs } = await getWritebacksRunHistory(
        writeback?.writeback_id ?? "",
        writebackRuns.length + 1
      );

      setWritebackRuns([...writebackRuns, ...writeback_runs]);
    }
  };

  // This logic is same as what we have in RunStatusDetails in addon.
  // Once this is a mono repo we can move the function to one place.
  const getRunStatus = (run: WritebackRun): WritebackRunOverallStatusType => {
    const runStatus = run.run_status;
    const totalRows = runStatus?.num_total_rows ?? 0;
    const successfulRows = runStatus?.num_successful_rows ?? 0;
    const skippedRows = runStatus?.num_skipped_rows ?? 0;

    if (totalRows === 0) {
      return WritebackRunOverallStatusType.AllRecordsFailed;
    }

    if (runStatus?.overall_status === WritebackRunOverallStatusType.Ok) {
      return WritebackRunOverallStatusType.Ok;
    }

    const failedRows = totalRows - successfulRows - skippedRows;

    if (failedRows === 0) {
      return WritebackRunOverallStatusType.Ok;
    }

    if (successfulRows === 0) {
      return WritebackRunOverallStatusType.AllRecordsFailed;
    }

    return WritebackRunOverallStatusType.SomeRecordsFailed;
  };

  const getRunStatusColor = (run: WritebackRun): string => {
    const runStatus = getRunStatus(run);
    switch (runStatus) {
      case WritebackRunOverallStatusType.Ok:
        return "green";
      case WritebackRunOverallStatusType.AllRecordsFailed:
        return "red";
      case WritebackRunOverallStatusType.SomeRecordsFailed:
        return "orange";
      default:
        return "red";
    }
  };

  const getRunStatusIcon = (run: WritebackRun): React.ReactNode => {
    const runStatus = getRunStatus(run);
    if (runStatus === WritebackRunOverallStatusType.Ok) {
      return <CheckCircleFilled />;
    }

    return <ExclamationCircleFilled />;
  };

  const getRunStatusText = (run: WritebackRun): string => {
    const runStatus = getRunStatus(run);
    switch (runStatus) {
      case WritebackRunOverallStatusType.Ok:
        return "Ok";
      case WritebackRunOverallStatusType.AllRecordsFailed:
        return "Failed";
      case WritebackRunOverallStatusType.SomeRecordsFailed:
        return "Warning";
      default:
        return "Failed";
    }
  };

  return (
    <div style={{ maxWidth: "calc(100% - 300px)", padding: "20px 30px" }}>
      {loading ? (
        <div style={{ margin: "5rem auto 2rem", textAlign: "center" }}>
          <LoaderWithPerfTimings name="WritebackDetails" size="large" />
        </div>
      ) : (
        <div>
          <Wrap>
            <Header>
              <LogoWrap>
                <ImportTypeIcon type={writeback?.data_source_type} />
                <PushIcon className="coeff__push_icon" />
              </LogoWrap>

              <div className="details">
                <Breadcrumb>
                  <BreadcrumbItem>
                    <Link to={{ pathname: `/dashboard/spreadsheets` }}>Spreadsheets</Link>
                  </BreadcrumbItem>

                  <BreadcrumbItem>
                    <Link
                      to={{
                        pathname: `/dashboard/spreadsheets/${spreadsheet?.google_sheets_file_id}`,
                        search: window.location.search,
                      }}
                    >
                      {spreadsheet?.google_sheets_file_name}
                    </Link>
                  </BreadcrumbItem>
                  <div style={{ visibility: "hidden" }}></div>
                </Breadcrumb>

                <h1>{writeback?.writeback_name}</h1>

                <span className="refreshDate">
                  {writeback?.last_run_dt &&
                    `Last exported ${getRefreshedDate(moment.utc(writeback?.last_run_dt).local())}`}
                  {!writeback?.last_run_dt &&
                    !writeback?.last_writeback_run_status?.status_dt &&
                    `Never exported`}
                </span>
              </div>

              <div>
                <Button type="primary" target="_blank" onClick={onOpenSheetClick}>
                  Open Sheet
                </Button>
              </div>
            </Header>

            <div style={{ marginLeft: "46px" }}>
              <div className="title">Run History</div>

              <Table
                dataSource={writebackRuns}
                locale={{ emptyText: "No export runs yet" }}
                pagination={
                  writebackRuns.length > 25
                    ? {
                        position: ["bottomCenter"],
                        size: "small",
                        pageSize: 25,
                        showSizeChanger: false,
                        onChange: onWritebackRunPageNumberChange,
                      }
                    : false
                }
                columns={[
                  {
                    title: "Status",
                    dataIndex: "status_type",
                    key: "status_type",
                    render: (_, record) => (
                      <FlexRow>
                        <StyledTag color={getRunStatusColor(record)}>
                          {getRunStatusIcon(record)}
                          <span className="text">{getRunStatusText(record)}</span>
                        </StyledTag>
                        <StyledLink>
                          {record.run_status?.overall_status !== "ok" &&
                            (Boolean(record.run_status?.title) ||
                              Boolean(record.run_status?.message)) && (
                              <a
                                style={{ marginLeft: 4 }}
                                href="javascript:void(0)"
                                onClick={() => {
                                  setShowFailedRunError(record.run_status!);
                                }}
                              >
                                View Details
                              </a>
                            )}
                        </StyledLink>
                      </FlexRow>
                    ),
                  },
                  {
                    title: "Time",
                    dataIndex: "end_dt",
                    key: "end_dt",
                    render: d => {
                      if (d) {
                        const date = moment(d);
                        return date.format("MMM DD, YYYY hh:mmA");
                      } else {
                        return "";
                      }
                    },
                  },
                  {
                    title: "Export Trigger",
                    dataIndex: "user",
                    key: "user",
                    render: (user, record) => {
                      return record.trigger_type === "scheduled"
                        ? "Scheduled"
                        : `Manual (${user?.display_name})`;
                    },
                  },
                  {
                    title: "Successful Rows",
                    dataIndex: "num_successful_rows",
                    key: "num_successful_rows",
                    className: "center",
                    render: num_successful_rows => num_successful_rows ?? 0,
                  },
                  {
                    title: "Failed Rows",
                    dataIndex: "num_rows",
                    key: "num_rows",
                    className: "center",
                    render: (num_rows, record) => {
                      return Boolean(num_rows)
                        ? num_rows -
                            (record.num_successful_rows ?? 0) -
                            (record.run_status?.num_skipped_rows ?? 0)
                        : 0;
                    },
                  },
                  {
                    title: "Skipped Rows",
                    dataIndex: "run_status",
                    key: "num_skipped_rows",
                    className: "center",
                    render: run_status => run_status?.num_skipped_rows ?? 0,
                  },
                  {
                    title: "Full Log",
                    dataIndex: "writeback_run_id",
                    key: "writeback_run_id",
                    render: writebackRunId => {
                      return (
                        <a
                          href="javascript:void(0)"
                          onClick={() => downloadRunHistory(writebackRunId)}
                        >
                          Full log (CSV)
                        </a>
                      );
                    },
                  },
                ]}
              />
            </div>
          </Wrap>

          <Sidebar>
            <WritebackDetailsSidebarContent writeback={writeback} />
          </Sidebar>
        </div>
      )}

      <ConfirmBox
        visible={
          Boolean(showFailedRunError) &&
          (Boolean(showFailedRunError?.title) || Boolean(showFailedRunError?.message))
        }
        closable={true}
        showCancel={false}
        onOk={() => setShowFailedRunError(null)}
        onCancel={() => setShowFailedRunError(null)}
        okText="Got it"
        title={showFailedRunError?.title}
        width={400}
      >
        <>
          {showFailedRunError?.overall_status === "some_records_failed" && (
            <WarningMessage {...showFailedRunError} />
          )}
          {showFailedRunError?.overall_status !== "some_records_failed" && (
            <ErrorMessage {...showFailedRunError} />
          )}
        </>
      </ConfirmBox>
    </div>
  );
};
